import { useContext, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import { nonNullable } from 'lib/array/arrayUtils'
import recalculatePointsEarnAfterBurn from 'loyalty/lib/pointsCalculation/recalculatePointsEarnAfterBurn'

const TIER_CONTENT_COLOUR: Record<App.LoyaltyTier, App.Design.ContentColour> = {
  bronze: 'tier-bronze-dark',
  silver: 'tier-silver-dark',
  gold: 'tier-gold-dark',
  platinum: 'tier-platinum',
  infinite: 'tier-infinite-dark',
}

const LoyaltyEarnSegment = styled.span`
  white-space: nowrap;
`

const messages = defineMessages({
  earnSocietePoints: {
    id: 'X5Jd57lIm_fQwpciuroy',
    defaultMessage: 'Earn {points} Société pts',
    description: 'Flight tile loyalty row showing earned Société points when no status credits apply',
  },
  earnStatusCredits: {
    id: 'kKH7U_4T68h6m8J0idfX',
    defaultMessage: '+ {statusCredits}',
    description: 'Flight tile loyalty row suffix showing earned status credits after Société points',
  },
})

interface Props {
  align?: 'start' | 'end'
  calculationRequest: App.LoyaltyPointsEarnCalculationRequest
  /**
   * When set, scales earned points and status credits based on the remaining
   * cash after points are applied. Mirrors `LoyaltyPointsEarn`'s burn scaling.
   */
  pointsToApply?: number
  /** Full burn price returned by the burn API for the displayed price. */
  burnPriceInPoints?: number
}

function FlightTileLoyaltyPoints({ align = 'end', calculationRequest, pointsToApply, burnPriceInPoints }: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const shouldHideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)

  const { totalPointsCalculation, pointsCalculations } = useLoyaltyPointsEarnCalculator(calculationRequest)

  const burnAdjustedTotals = useMemo(() => {
    if (!pointsToApply || pointsToApply <= 0 || !burnPriceInPoints || burnPriceInPoints <= 0) return undefined

    const completedCalculations = nonNullable(pointsCalculations.map(pointsCalculation => pointsCalculation?.data))
    if (!completedCalculations.length) return undefined

    return recalculatePointsEarnAfterBurn(completedCalculations, pointsToApply, burnPriceInPoints)
  }, [burnPriceInPoints, pointsCalculations, pointsToApply])

  const points = burnAdjustedTotals?.points ?? totalPointsCalculation.data?.points ?? 0

  const statusCredits = shouldHideStatusCredits
    ? 0
    : burnAdjustedTotals
      ? burnAdjustedTotals.statusCredits + burnAdjustedTotals.bonusStatusCredits
      : (totalPointsCalculation.data?.statusCredits ?? 0) + (totalPointsCalculation.data?.bonusStatusCredits ?? 0)

  const formattedPoints = intl.formatNumber(points)
  const statusCreditsLabel = <FormatLoyaltyStatusCredits statusCredits={statusCredits} />
  const earnPointsLabel = intl.formatMessage(messages.earnSocietePoints, { points: formattedPoints })
  const earnStatusCreditsLabel = statusCredits > 0
    ? intl.formatMessage(messages.earnStatusCredits, { statusCredits: statusCreditsLabel })
    : undefined

  if (
    !loyaltyEnabled ||
    totalPointsCalculation.error ||
    (!!accountRegion && accountRegion !== currentRegionCode)
  ) return null

  if (totalPointsCalculation.initial || totalPointsCalculation.fetching) {
    return <TextLoadingBox typography="caption-smallalt" width="20ch" />
  }

  if (!points) return null

  return <Caption variant="smallalt" colour={TIER_CONTENT_COLOUR[displayTier]} align={align}>
    <LoyaltyEarnSegment>{earnPointsLabel}</LoyaltyEarnSegment>
    {earnStatusCreditsLabel && <>
      {' '}
      <LoyaltyEarnSegment>{earnStatusCreditsLabel}</LoyaltyEarnSegment>
    </>}
  </Caption>
}

export default FlightTileLoyaltyPoints
